/**
 * NeoCoreCobranzaPage — módulo de cobranza del operador.
 *
 * Muestra una BANDEJA priorizada (1 fila por crédito activo) con la próxima
 * cuota, aging (días para/desde el vencimiento), última gestión y promesa
 * vigente. Desde el drawer el operador:
 *   - Registra una GESTIÓN (canal + resultado + notas), y si el resultado es
 *     "promesa de pago", crea la PROMESA (monto + fecha) atada.
 *   - Manda un recordatorio manual por WhatsApp (wa.me) con texto pre-armado.
 *   - Marca promesas cumplidas / rotas.
 *
 * Backend: RPCs de la migración 0218 (cobranza_bandeja, cobranza_resumen,
 * cobranza_historial, registrar_gestion_cobro, actualizar_promesa_pago,
 * marcar_promesas_vencidas). Gate is_admin_or_contador().
 *
 * Enfoque v1: cartera hoy 100% "por vencer" → cobranza PREVENTIVA. La UI ya
 * soporta mora/aging para cuando llegue.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowsClockwise, MagnifyingGlass, Coins, WhatsappLogo, ClockCountdown, Confetti, HandHeart, Phone } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { useRiesgoCartera, RiesgoCarteraStrip } from '@/components/RiesgoCartera'
import { ReferenciasCobranza } from '@/components/ReferenciasCobranza'

type BandejaRow = {
  credito_id: string
  asociado_id: string
  no_asociado: string
  nombre_completo: string
  telefono_e164: string | null
  whatsapp_optin: boolean
  canal_preferido: string | null
  saldo_capital: number
  total_cuotas: number
  cuotas_pagadas: number
  proxima_numero: number | null
  proxima_vencimiento: string | null
  proxima_monto: number | null
  dpd: number | null
  estado_cobranza: string
  bucket: string
  cuotas_vencidas: number
  monto_vencido: number
  ultima_gestion_at: string | null
  ultima_gestion_canal: string | null
  ultima_gestion_resultado: string | null
  proximo_seguimiento: string | null
  promesa_id: string | null
  promesa_fecha: string | null
  promesa_monto: number | null
  promesa_estado: string | null
  prioridad: number
  telefono_verificado_at: string | null
}

type Resumen = {
  creditos_activos: number
  saldo_cartera: number
  por_vencer_7d_monto: number
  por_vencer_7d_cuotas: number
  vence_hoy_monto: number
  vencido_monto: number
  vencido_creditos: number
  promesas_vigentes: number
  promesas_vigentes_monto: number
  promesas_rotas_30d: number
}

const fmtQ = (n: number | null) =>
  'Q ' + Number(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtFecha = (iso: string | null) => {
  if (!iso) return '—'
  return new Date(iso + (iso.length === 10 ? 'T12:00:00' : '')).toLocaleDateString('es-GT', {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Guatemala',
  })
}
const fmtFechaHora = (iso: string | null) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-GT', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'America/Guatemala',
  })
}

// Estado de cobranza → etiqueta + color del badge.
const ESTADO_META: Record<string, { label: string; color: string; bg: string }> = {
  vencida:            { label: 'Vencida', color: 'var(--danger, #a32d2d)', bg: 'rgba(163,45,45,0.10)' },
  vence_hoy:          { label: 'Vence hoy', color: 'var(--warning, #d97706)', bg: 'rgba(217,119,6,0.12)' },
  por_vencer_pronto:  { label: 'Por vencer', color: 'var(--warning, #d97706)', bg: 'rgba(217,119,6,0.10)' },
  por_vencer:         { label: 'Al día', color: 'var(--ok-ink, #0f8a57)', bg: 'rgba(15,138,87,0.10)' },
  sin_pendiente:      { label: 'Sin cuota', color: 'var(--muted-foreground)', bg: 'var(--n-100)' },
}

const RESULTADO_LABEL: Record<string, string> = {
  contactado: 'Contactado', promesa_pago: 'Promesa de pago', no_contesta: 'No contesta',
  buzon: 'Buzón', numero_equivocado: 'Número equivocado', negativa_pago: 'Negativa', ya_pago: 'Ya pagó', otro: 'Otro',
}
const CANAL_LABEL: Record<string, string> = {
  whatsapp: 'WhatsApp', llamada: 'Llamada', sms: 'SMS', email: 'Email', presencial: 'Presencial', push: 'Push', otro: 'Otro',
}

// "días para/desde vencer" legible a partir de dpd (días pasados del venc.).
const dpdTexto = (dpd: number | null) => {
  if (dpd === null) return '—'
  if (dpd > 0) return `${dpd} ${dpd === 1 ? 'día' : 'días'} de atraso`
  if (dpd === 0) return 'vence hoy'
  const d = -dpd
  return `en ${d} ${d === 1 ? 'día' : 'días'}`
}

// wa.me a partir del E164 (quita el '+' y no-dígitos).
const waLink = (e164: string | null, texto: string) => {
  if (!e164) return null
  const num = e164.replace(/[^0-9]/g, '')
  if (!num) return null
  return `https://wa.me/${num}?text=${encodeURIComponent(texto)}`
}
// Natalie lleva la comunicación con la comunidad de Neocoop: los mensajes
// salen a su nombre, en primera persona, 1-a-1. (Editá REMITENTE cuando cambie
// quién comunica.) El framing NO es "cobro": es un toque de comunidad — en la
// cooperativa lo que cada quien paga a tiempo vuelve al fondo para el siguiente.
const REMITENTE = 'Natalie'

// Mensajes diseñados con palancas conductuales (evidencia de nudges de pago),
// para bajar la fricción, no para "cobrar":
//  · Personalización (nombre) → sube la respuesta vs. genérico.
//  · Intención de implementación → que la persona elija CUÁNDO ("¿hoy o el
//    viernes?"): comprometerse a un momento dispara la acción.
//  · Reducir el esfuerzo percibido → "dos toques", "un minuto", "sacarlo de
//    la lista" (cerrar el bucle abierto se siente bien).
//  · Sin culpa en la mora → la vergüenza dispara evasión; "le pasa a cualquiera"
//    + "quedás al corriente" (borrón y cuenta nueva) baja la reactancia.
//  · Autonomía → ofrecer mover la fecha; nada de presión ni de apelaciones
//    "prosociales" ("tu pago ayuda a otro"), que en experimentos fallan.
// Voz real de Neocoop (banco de líneas del feed, adn-y-voz-neocoop.md):
// aspiracional, irónica, con clase, de vos. Recurso propio "No es X. Es Y."
// y líneas suyas ("el drama, para otra app", "en lo que dura un scroll",
// "un nivel que te ganaste"). El pago no es cobro: es subir de nivel en el club.
// Debajo siguen las palancas — reencuadre, esfuerzo mínimo, autonomía, mora
// sin lástima, identidad/estatus al pagar.
const mensajeCelebracion = (nombre: string, cerrado: boolean) => {
  const n = (nombre || '').split(' ')[0]
  if (cerrado) {
    return `Hola ${n}, crédito saldado. Ya no sos el que pide Q500: sos el que decide hasta dónde llega. Tu próximo nivel te espera cuando quieras. — ${REMITENTE}, Neocoop`
  }
  return `Hola ${n}, pagaste a tiempo. Eso no es una cuota menos: es un nivel que te ganaste. — ${REMITENTE}, Neocoop`
}

// Formas de pago institucionales (cuenta_bancaria_propia_publica, solo GTQ).
// Orden: cuentas primero, luego Akisí (efectivo), luego RapiBAC. Si cambian, editá acá.
const TITULAR = 'Cooperativa Integral de Ahorro y Crédito NEO, R.L.'
const CUENTAS_PAGO = [
  { banco: 'Banco Internacional', tipo: 'Ahorro',    numero: '7101905269' },
  { banco: 'BAC',                 tipo: 'Monetaria', numero: '907312102' },
]

// Mensaje "cómo pagar" — en la voz de Natalie. Cuentas primero, luego Akisí,
// luego RapiBAC. Cierra con el pago real: subir el comprobante en la app, o
// preguntar. (La app es el canal; wa solo si tienen dudas.)
const mensajeFormasDePago = (r: BandejaRow) => {
  const nombre = (r.nombre_completo || '').split(' ')[0]
  const monto = fmtQ(r.proxima_monto)
  return `Hola ${nombre}, para pagar tu cuota de ${monto} en Neocoop podés:\n\n`
    + `Transferencia o depósito a cualquiera de nuestras cuentas (a nombre de ${TITULAR}):\n`
    + `· Banco Internacional Ahorro: 7101905269\n`
    + `· BAC Monetaria: 907312102\n\n`
    + `En efectivo, en cualquier punto Akisí.\n`
    + `También por RapiBAC a la cuenta BAC de arriba.\n\n`
    + `Cuando pagués, subí la foto del comprobante en la app, en Reportar pago. Si tenés dudas, escribime por acá y te ayudo.\n\n`
    + `Y acordate: pagando puntual podés volver a pedir tus Q500 y vas subiendo de nivel. — ${REMITENTE}, Neocoop`
}

// tel: para tap-to-call desde el drawer (quita no-dígitos, conserva el '+').
const telLink = (e164: string | null) => {
  if (!e164) return null
  const num = e164.replace(/[^0-9+]/g, '')
  return num ? `tel:${num}` : null
}

// Saludo suelto por WhatsApp (sin agenda de cobro) para abrir conversación.
const mensajeSaludo = (nombre: string) =>
  `Hola ${(nombre || '').split(' ')[0]}, soy ${REMITENTE} de Neocoop. ¿Cómo estás? Te escribo para ver si puedo ayudarte en algo con tu cuenta.`

// Recordatorios manuales — pre-armados según el estado de la cuota, en la voz de
// Natalie (palancas conductuales: momento elegido, esfuerzo mínimo, mora sin
// lástima, identidad/nivel). El operador elige y envía; queda registrado.
const recordatoriosPara = (r: BandejaRow): Array<{ key: string; label: string; texto: string }> => {
  const n = (r.nombre_completo || '').split(' ')[0]
  const monto = fmtQ(r.proxima_monto)
  const fecha = fmtFecha(r.proxima_vencimiento)
  const firma = `— ${REMITENTE}, Neocoop`
  if (r.estado_cobranza === 'vencida') {
    return [{
      key: 'vencida',
      label: 'Vencida · sin lástima',
      texto: `Hola ${n}, se pasó la fecha de tu cuota de ${monto} — le pasa a cualquiera. Si la pagás hoy quedás al corriente, borrón y cuenta nueva, y seguís habilitado para tus próximos Q500. ¿Te paso cómo? ${firma}`,
    }]
  }
  if (r.estado_cobranza === 'vence_hoy') {
    return [{
      key: 'vence_hoy',
      label: 'Vence hoy',
      texto: `Hola ${n}, hoy vence tu cuota de ${monto}. En lo que dura un scroll la dejás lista y no cortás la racha. ¿La pasás hoy? ${firma}`,
    }]
  }
  // por vencer / al día
  return [{
    key: 'por_vencer',
    label: 'Por vencer',
    texto: `Hola ${n}, tu cuota de ${monto} vence el ${fecha}. ¿La dejamos lista hoy o preferís el día del vencimiento? Vos elegís; con dos toques queda. ${firma}`,
  }]
}

type Filtro = 'prioritarias' | 'por_vencer' | 'vencidas' | 'promesas' | 'todos'

export function NeoCoreCobranzaPage() {
  const [rows, setRows] = useState<BandejaRow[]>([])
  const [resumen, setResumen] = useState<Resumen | null>(null)
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState<Filtro>('prioritarias')
  const [search, setSearch] = useState('')
  const [drawer, setDrawer] = useState<BandejaRow | null>(null)
  const [vista, setVista] = useState<'bandeja' | 'celebrar' | 'reporte'>('bandeja')

  const load = useCallback(async () => {
    setLoading(true)
    // Marca promesas vencidas como rotas antes de leer (idempotente).
    await supabase.rpc('marcar_promesas_vencidas')
    const [{ data: bandeja }, { data: res }] = await Promise.all([
      supabase.rpc('cobranza_bandeja'),
      supabase.rpc('cobranza_resumen'),
    ])
    setRows((bandeja as BandejaRow[]) ?? [])
    setResumen((res as Resumen) ?? null)
    setLoading(false)
  }, [])

  useEffect(() => { void load() }, [load])

  // Al mandar el WhatsApp: registra la gestión (queda el rastro) y refresca —
  // la fila NO se quita, solo pasa a mostrar "Última gestión". Fire-and-forget
  // para no bloquear la apertura de WhatsApp.
  const registrarEnvioWa = useCallback((creditoId: string) => {
    supabase.rpc('registrar_gestion_cobro', {
      p_credito_id: creditoId, p_canal: 'whatsapp', p_resultado: 'contactado',
      p_notas: 'Recordatorio de pago enviado por WhatsApp',
    }).then(() => { void load() }, () => {})
  }, [load])

  const counts = useMemo(() => ({
    prioritarias: rows.length,
    por_vencer: rows.filter((r) => r.estado_cobranza.startsWith('por_vencer') || r.estado_cobranza === 'vence_hoy').length,
    vencidas: rows.filter((r) => r.estado_cobranza === 'vencida').length,
    promesas: rows.filter((r) => !!r.promesa_id).length,
    todos: rows.length,
  }), [rows])

  const filtered = useMemo(() => {
    let list = rows
    if (filtro === 'por_vencer') list = rows.filter((r) => r.estado_cobranza.startsWith('por_vencer') || r.estado_cobranza === 'vence_hoy')
    else if (filtro === 'vencidas') list = rows.filter((r) => r.estado_cobranza === 'vencida')
    else if (filtro === 'promesas') list = rows.filter((r) => !!r.promesa_id)
    const q = search.trim().toLowerCase()
    if (q) {
      list = list.filter((r) =>
        r.nombre_completo.toLowerCase().includes(q) ||
        r.no_asociado.toLowerCase().includes(q))
    }
    return list
  }, [rows, filtro, search])

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 18, gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em' }}>Cobranza</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--muted-foreground)' }}>
            Bandeja priorizada de cuotas · gestión, promesas y recordatorios.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 2, background: 'var(--bg)', border: '1px solid var(--hairline)', borderRadius: 99, padding: 2 }}>
            {(['bandeja', 'celebrar', 'reporte'] as const).map((v) => (
              <button key={v} onClick={() => setVista(v)} style={{
                all: 'unset', cursor: 'pointer', padding: '5px 14px', borderRadius: 99, fontSize: 12, fontWeight: 700,
                background: vista === v ? 'var(--brand)' : 'transparent', color: vista === v ? '#fff' : 'var(--ink-soft)',
              }}>{v === 'bandeja' ? 'Bandeja' : v === 'celebrar' ? 'Celebrar' : 'Reporte'}</button>
            ))}
          </div>
          <button onClick={() => void load()} className="btn btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <ArrowsClockwise size={13} weight="bold" /> Refrescar
          </button>
        </div>
      </div>

      {/* KPIs */}
      {resumen && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
          <Kpi label="Cartera activa" value={fmtQ(resumen.saldo_cartera)} sub={`${resumen.creditos_activos} créditos`} />
          <Kpi label="Por vencer (7 días)" value={fmtQ(resumen.por_vencer_7d_monto)} sub={`${resumen.por_vencer_7d_cuotas} cuotas`} tone="warn" />
          <Kpi label="Vencido" value={fmtQ(resumen.vencido_monto)} sub={`${resumen.vencido_creditos} créditos`} tone={resumen.vencido_monto > 0 ? 'danger' : undefined} />
          <Kpi label="Promesas vigentes" value={fmtQ(resumen.promesas_vigentes_monto)} sub={`${resumen.promesas_vigentes} promesas`} tone="ok" />
        </div>
      )}

      {vista === 'reporte' ? (
        <ReportePanel />
      ) : vista === 'celebrar' ? (
        <CelebrarPanel />
      ) : (
      <>
      {/* Filtros + búsqueda */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 12,
        padding: '12px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {([
            ['prioritarias', 'Prioritarias', counts.prioritarias],
            ['por_vencer', 'Por vencer', counts.por_vencer],
            ['vencidas', 'Vencidas', counts.vencidas],
            ['promesas', 'Con promesa', counts.promesas],
            ['todos', 'Todos', counts.todos],
          ] as Array<[Filtro, string, number]>).map(([key, label, n]) => {
            const active = filtro === key
            return (
              <button key={key} onClick={() => setFiltro(key)} style={{
                all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 99,
                background: active ? 'var(--brand-50)' : 'transparent',
                color: active ? 'var(--brand-700)' : 'var(--ink-soft)',
                fontSize: 12, fontWeight: 700,
                border: '1px solid', borderColor: active ? 'var(--brand-300, transparent)' : 'transparent',
              }}>
                {label}
                <span style={{
                  fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 99,
                  background: active ? 'var(--brand)' : 'var(--n-100)', color: active ? '#fff' : 'var(--muted-foreground)',
                }}>{n}</span>
              </button>
            )
          })}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg)', borderRadius: 8,
          padding: '6px 10px', border: '1px solid var(--hairline)', minWidth: 240,
        }}>
          <MagnifyingGlass size={14} weight="bold" color="var(--muted-foreground)" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Asociado o número…"
            style={{ flex: 1, border: 0, background: 'transparent', outline: 'none', fontSize: 13, fontFamily: 'inherit', color: 'var(--ink)' }} />
        </div>
      </div>

      {/* Tabla */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>
            <Coins size={28} weight="duotone" /><br />Sin créditos con este filtro.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--hairline)', background: 'var(--bg)' }}>
                <th style={th}>Asociado</th>
                <th style={th}>Próxima cuota</th>
                <th style={{ ...th, textAlign: 'right' }}>Monto</th>
                <th style={th}>Estado</th>
                <th style={th}>Última gestión</th>
                <th style={th}>Promesa</th>
                <th style={{ ...th, textAlign: 'right' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const meta = ESTADO_META[r.estado_cobranza] ?? ESTADO_META.sin_pendiente
                const wa = waLink(r.telefono_e164, mensajeFormasDePago(r))
                return (
                  <tr key={r.credito_id} style={{ borderBottom: '1px solid var(--line-soft)' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.nombre_completo}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
                        {r.no_asociado} · cuota {(r.cuotas_pagadas ?? 0) + (r.proxima_numero ? 0 : 0) + 1}/{r.total_cuotas}
                      </div>
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600 }}>{fmtFecha(r.proxima_vencimiento)}</div>
                      <div style={{ fontSize: 11, color: r.estado_cobranza === 'vencida' ? 'var(--danger, #a32d2d)' : 'var(--muted-foreground)' }}>{dpdTexto(r.dpd)}</div>
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtQ(r.proxima_monto)}</td>
                    <td style={td}>
                      <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 99, background: meta.bg, color: meta.color, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
                        {meta.label}
                      </span>
                    </td>
                    <td style={{ ...td, fontSize: 12 }}>
                      {r.ultima_gestion_at ? (
                        <>
                          <div>{RESULTADO_LABEL[r.ultima_gestion_resultado ?? ''] ?? '—'}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{fmtFechaHora(r.ultima_gestion_at)}</div>
                        </>
                      ) : <span style={{ color: 'var(--muted-foreground)' }}>Sin gestión</span>}
                    </td>
                    <td style={{ ...td, fontSize: 12 }}>
                      {r.promesa_id ? (
                        <>
                          <div style={{ fontWeight: 600 }}>{fmtQ(r.promesa_monto)}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{fmtFecha(r.promesa_fecha)}</div>
                        </>
                      ) : <span style={{ color: 'var(--muted-foreground)' }}>—</span>}
                    </td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {wa && (
                        <a href={wa} target="_blank" rel="noreferrer" title="Enviar por WhatsApp (queda registrado como gestión)"
                          onClick={() => registrarEnvioWa(r.credito_id)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', marginRight: 6, borderRadius: 8, border: '1px solid rgba(15,138,87,0.30)', color: 'var(--ok-ink, #0f8a57)', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                          <WhatsappLogo size={14} weight="fill" /> WhatsApp
                        </a>
                      )}
                      <button onClick={() => setDrawer(r)} className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }}>Gestionar</button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
      </>
      )}

      {drawer && (
        <CobranzaDrawer row={drawer} onClose={() => setDrawer(null)} onChange={() => { void load() }} />
      )}
    </div>
  )
}

// ============================================================
// Panel · Reporte de cartera (aging + efectividad de promesas)
// ============================================================
type AgingRow = { bucket: string; etiqueta: string; cuotas: number; creditos: number; monto: number }
type RepPromesas = { vigentes: number; vigentes_monto: number; cumplidas_30d: number; rotas_30d: number; total_cerradas_30d: number; gestiones_30d: number }

function ReportePanel() {
  const riesgo = useRiesgoCartera()  // clasificación de reporte (días calendario, 0392)
  const [aging, setAging] = useState<AgingRow[]>([])
  const [prom, setProm] = useState<RepPromesas | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancel = false
    ;(async () => {
      const [{ data: a }, { data: p }] = await Promise.all([
        supabase.rpc('cobranza_reporte_aging'),
        supabase.rpc('cobranza_reporte_promesas'),
      ])
      if (cancel) return
      setAging((a as AgingRow[]) ?? [])
      setProm((p as RepPromesas) ?? null)
      setLoading(false)
    })()
    return () => { cancel = true }
  }, [])

  const totalMonto = aging.reduce((s, r) => s + Number(r.monto), 0)
  const enMora = aging.filter((r) => r.bucket !== '0_al_dia').reduce((s, r) => s + Number(r.monto), 0)
  const efectividad = prom && prom.total_cerradas_30d > 0 ? Math.round((prom.cumplidas_30d / prom.total_cerradas_30d) * 100) : null

  const barColor: Record<string, string> = {
    '0_al_dia': 'var(--ok-ink, #0f8a57)', '1_1-7': 'var(--warning, #d97706)', '2_8-30': 'var(--warning, #d97706)',
    '3_31-60': 'var(--brand)', '4_61-90': 'var(--danger, #a32d2d)', '5_90+': 'var(--danger, #a32d2d)',
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando reporte…</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <RiesgoCarteraStrip rows={riesgo.rows} />
      {/* Aging */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 12, padding: '16px 18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800 }}>Aging de cartera</h3>
          <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
            Vencido (gestión): <strong style={{ color: enMora > 0 ? 'var(--danger, #a32d2d)' : 'var(--ok-ink, #0f8a57)' }}>{fmtQ(enMora)}</strong> · Total pendiente {fmtQ(totalMonto)}
          </span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--hairline)' }}>
              <th style={th}>Tramo</th>
              <th style={{ ...th, textAlign: 'right' }}>Créditos</th>
              <th style={{ ...th, textAlign: 'right' }}>Cuotas</th>
              <th style={{ ...th, textAlign: 'right' }}>Monto</th>
              <th style={{ ...th, width: '30%' }}>%</th>
            </tr>
          </thead>
          <tbody>
            {aging.map((r) => {
              const pct = totalMonto > 0 ? (Number(r.monto) / totalMonto) * 100 : 0
              return (
                <tr key={r.bucket} style={{ borderBottom: '1px solid var(--line-soft)' }}>
                  <td style={td}>{r.etiqueta}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{r.creditos}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{r.cuotas}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmtQ(r.monto)}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, height: 8, borderRadius: 99, background: 'var(--n-100)', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: barColor[r.bucket] ?? 'var(--brand)' }} />
                      </div>
                      <span style={{ fontSize: 11, color: 'var(--muted-foreground)', minWidth: 34, textAlign: 'right' }}>{pct.toFixed(0)}%</span>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Efectividad de promesas + gestiones */}
      {prom && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          <Kpi label="Promesas vigentes" value={fmtQ(prom.vigentes_monto)} sub={`${prom.vigentes} activas`} tone="ok" />
          <Kpi label="Cumplidas (30 días)" value={String(prom.cumplidas_30d)} sub={efectividad !== null ? `${efectividad}% efectividad` : 'sin cierres aún'} tone="ok" />
          <Kpi label="Rotas (30 días)" value={String(prom.rotas_30d)} sub="promesas incumplidas" tone={prom.rotas_30d > 0 ? 'danger' : undefined} />
          <Kpi label="Gestiones (30 días)" value={String(prom.gestiones_30d)} sub="contactos registrados" />
        </div>
      )}

      <p style={{ margin: 0, fontSize: 11, color: 'var(--muted-foreground)' }}>
        La cartera hoy está sin mora — los tramos vencidos se prenden cuando pase la primera fecha de pago sin cubrir.
      </p>
    </div>
  )
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'warn' | 'danger' | 'ok' }) {
  const color = tone === 'warn' ? 'var(--warning, #d97706)' : tone === 'danger' ? 'var(--danger, #a32d2d)' : tone === 'ok' ? 'var(--ok-ink, #0f8a57)' : 'var(--muted-foreground)'
  const border = tone === 'danger' ? 'rgba(163,45,45,0.30)' : tone === 'warn' ? 'rgba(217,119,6,0.25)' : 'var(--hairline)'
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid', borderColor: border, borderRadius: 12, padding: '14px 16px' }}>
      <p style={{ margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: 1.1, textTransform: 'uppercase', color }}>{label}</p>
      <p style={{ margin: '6px 0 0', fontSize: 21, fontWeight: 800, color: 'var(--ink)', fontFeatureSettings: '"tnum"' }}>{value}</p>
      {sub && <p style={{ margin: '2px 0 0', fontSize: 11, fontWeight: 600, color: 'var(--muted-foreground)' }}>{sub}</p>}
    </div>
  )
}

const th: React.CSSProperties = { textAlign: 'left', padding: '10px 14px', fontSize: 10, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2 }
const td: React.CSSProperties = { padding: '12px 14px' }

// ============================================================
// Panel · Celebrar (Loop A · pagó a tiempo → reconocimiento de Natalie)
// ============================================================
type CelebrarRow = {
  credito_id: string; asociado_id: string; no_asociado: string; nombre_completo: string
  telefono_e164: string | null; whatsapp_optin: boolean
  cuota_id: string; cuota_numero: number; monto: number; pagada_at: string
  fecha_vencimiento: string; dias_anticipacion: number
  cuotas_pagadas: number; total_cuotas: number; credito_cerrado: boolean
}

function CelebrarPanel() {
  const [rows, setRows] = useState<CelebrarRow[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.rpc('cobranza_para_celebrar')
    setRows((data as CelebrarRow[]) ?? [])
    setLoading(false)
  }, [])
  useEffect(() => { void load() }, [load])

  const marcar = async (r: CelebrarRow) => {
    setBusy(r.cuota_id)
    const { error } = await supabase.rpc('registrar_celebracion', {
      p_credito_id: r.credito_id, p_cuota_id: r.cuota_id, p_canal: 'whatsapp',
    })
    setBusy(null)
    if (error) { alert('Error: ' + error.message); return }
    setRows((prev) => prev.filter((x) => x.cuota_id !== r.cuota_id))
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)' }}>Cargando…</div>

  return (
    <div>
      <div style={{
        background: 'rgba(15,138,87,0.06)', border: '1px solid rgba(15,138,87,0.20)', borderRadius: 12,
        padding: '14px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <Confetti size={22} weight="fill" color="var(--ok-ink, #0f8a57)" />
        <div>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>
            {rows.length === 0 ? 'Todo celebrado por ahora' : `${rows.length} ${rows.length === 1 ? 'asociado pagó' : 'asociados pagaron'} a tiempo`}
          </p>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>
            Pagar bien no es solo cumplir — es el motor del club. Felicitá de parte de Natalie y marcá el toque.
          </p>
        </div>
      </div>

      {rows.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted-foreground)', background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 12 }}>
          <Confetti size={28} weight="duotone" /><br />No hay pagos nuevos para celebrar.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {rows.map((r) => {
            const wa = waLink(r.telefono_e164, mensajeCelebracion(r.nombre_completo, r.credito_cerrado))
            return (
              <div key={r.cuota_id} style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{r.nombre_completo}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{r.no_asociado}</div>
                  </div>
                  {r.credito_cerrado ? (
                    <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 9px', borderRadius: 99, background: 'rgba(240,78,35,0.10)', color: 'var(--brand)', textTransform: 'uppercase' }}>Crédito saldado</span>
                  ) : (
                    <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 9px', borderRadius: 99, background: 'rgba(15,138,87,0.10)', color: 'var(--ok-ink, #0f8a57)', textTransform: 'uppercase' }}>A tiempo</span>
                  )}
                </div>
                <div style={{ margin: '10px 0', fontSize: 12, color: 'var(--ink-soft)' }}>
                  Cuota {r.cuota_numero} · <strong>{fmtQ(r.monto)}</strong> · pagó el {fmtFecha(r.pagada_at)}
                  {r.dias_anticipacion > 0 && <span style={{ color: 'var(--ok-ink, #0f8a57)' }}> · {r.dias_anticipacion} {r.dias_anticipacion === 1 ? 'día' : 'días'} antes</span>}
                  <br /><span style={{ color: 'var(--muted-foreground)' }}>{r.cuotas_pagadas}/{r.total_cuotas} cuotas pagadas</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {wa && (
                    <a href={wa} target="_blank" rel="noreferrer" className="btn"
                      style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#25D366', color: '#fff', border: 0, fontWeight: 700, fontSize: 12 }}>
                      <WhatsappLogo size={14} weight="fill" /> Felicitar
                    </a>
                  )}
                  <button onClick={() => void marcar(r)} disabled={busy === r.cuota_id} className="btn btn-ghost" style={{ fontSize: 12 }}>
                    {busy === r.cuota_id ? '…' : 'Marcar ✓'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ============================================================
// Drawer · gestión + promesa + historial
// ============================================================
type Historial = {
  gestiones: Array<{ id: string; fecha: string; canal: string; resultado: string; notas: string | null; proximo_seguimiento: string | null }>
  promesas: Array<{ id: string; fecha_promesa: string; monto: number; estado: string; monto_cumplido: number; creada: string }>
}

type MoraResumen = {
  mora_pendiente: number
  mora_condonada_total: number
  cuotas: Array<{ numero: number; mora_pendiente: number; dias_atraso: number | null; condonada: boolean }>
}

const CANALES: Array<{ v: string; l: string }> = [
  { v: 'whatsapp', l: 'WhatsApp' }, { v: 'llamada', l: 'Llamada' }, { v: 'sms', l: 'SMS' },
  { v: 'presencial', l: 'Presencial' }, { v: 'email', l: 'Email' }, { v: 'otro', l: 'Otro' },
]
const RESULTADOS: Array<{ v: string; l: string }> = [
  { v: 'contactado', l: 'Contactado' }, { v: 'promesa_pago', l: 'Promesa de pago' }, { v: 'no_contesta', l: 'No contesta' },
  { v: 'buzon', l: 'Buzón' }, { v: 'numero_equivocado', l: 'Número equivocado' }, { v: 'negativa_pago', l: 'Negativa' },
  { v: 'ya_pago', l: 'Ya pagó' }, { v: 'otro', l: 'Otro' },
]

function CobranzaDrawer({ row, onClose, onChange }: { row: BandejaRow; onClose: () => void; onChange: () => void }) {
  const [submitting, setSubmitting] = useState(false)
  const [hist, setHist] = useState<Historial | null>(null)
  const [mora, setMora] = useState<MoraResumen | null>(null)
  const [showCondonar, setShowCondonar] = useState(false)
  const [canal, setCanal] = useState('whatsapp')
  const [resultado, setResultado] = useState('contactado')
  const [notas, setNotas] = useState('')
  const [proxSeg, setProxSeg] = useState('')
  const [promesaMonto, setPromesaMonto] = useState('')
  const [promesaFecha, setPromesaFecha] = useState('')

  const loadHist = useCallback(async () => {
    const { data } = await supabase.rpc('cobranza_historial', { p_credito_id: row.credito_id })
    setHist((data as Historial) ?? { gestiones: [], promesas: [] })
  }, [row.credito_id])
  const loadMora = useCallback(async () => {
    const { data } = await supabase.rpc('cobranza_mora_credito', { p_credito_id: row.credito_id })
    setMora((data as MoraResumen) ?? null)
  }, [row.credito_id])
  useEffect(() => { void loadHist(); void loadMora() }, [loadHist, loadMora])

  const meta = ESTADO_META[row.estado_cobranza] ?? ESTADO_META.sin_pendiente
  const waFormas = waLink(row.telefono_e164, mensajeFormasDePago(row))
  const tel = telLink(row.telefono_e164)
  const waSaludo = waLink(row.telefono_e164, mensajeSaludo(row.nombre_completo))
  const esPromesa = resultado === 'promesa_pago'

  // Verificación del número (ADR-019 · etapa 1). Nataly marca verificado cuando
  // confirma que el número responde al contactar; feedback local inmediato.
  const [verifAt, setVerifAt] = useState<string | null>(row.telefono_verificado_at)
  const [verificando, setVerificando] = useState(false)
  const marcarVerificado = async () => {
    setVerificando(true)
    const { error } = await supabase.rpc('marcar_telefono_verificado_admin', {
      p_asociado_id: row.asociado_id, p_nota: 'Verificado desde cobranza',
    })
    setVerificando(false)
    if (error) { alert('No se pudo marcar verificado: ' + error.message); return }
    setVerifAt(new Date().toISOString())
    onChange()
  }

  // Registra un contacto por WhatsApp como gestión (fire-and-forget).
  const registrarWa = (notas: string) => {
    supabase.rpc('registrar_gestion_cobro', {
      p_credito_id: row.credito_id, p_canal: 'whatsapp', p_resultado: 'contactado', p_notas: notas,
    }).then(() => onChange(), () => {})
  }

  const guardar = async () => {
    if (esPromesa && (!promesaMonto || !promesaFecha)) { alert('Ingresá monto y fecha de la promesa'); return }
    setSubmitting(true)
    const { data, error } = await supabase.rpc('registrar_gestion_cobro', {
      p_credito_id: row.credito_id,
      p_canal: canal,
      p_resultado: resultado,
      p_cuota_id: null,
      p_notas: notas || null,
      p_proximo_seguimiento: proxSeg || null,
      p_promesa_monto: esPromesa ? Number(promesaMonto) : null,
      p_promesa_fecha: esPromesa ? promesaFecha : null,
    })
    setSubmitting(false)
    if (error) { alert('Error: ' + error.message); return }
    const res = data as { ok?: boolean; error?: string } | null
    if (res && res.ok === false) { alert('No se pudo: ' + (res.error ?? 'error')); return }
    setNotas(''); setProxSeg(''); setPromesaMonto(''); setPromesaFecha(''); setResultado('contactado')
    await loadHist()
    onChange()
  }

  const cerrarPromesa = async (id: string, estado: 'cumplida' | 'rota' | 'cancelada') => {
    setSubmitting(true)
    const { error } = await supabase.rpc('actualizar_promesa_pago', { p_promesa_id: id, p_estado: estado, p_monto_cumplido: null })
    setSubmitting(false)
    if (error) { alert('Error: ' + error.message); return }
    await loadHist(); onChange()
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,18,25,0.40)', zIndex: 999 }} />
      <aside style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(540px, 100%)', background: '#fff', zIndex: 1000, display: 'flex', flexDirection: 'column', boxShadow: '-12px 0 32px rgba(0,0,0,0.10)' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--hairline)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>{row.nombre_completo}</h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>
              {row.no_asociado} · {row.telefono_e164 ?? 'sin teléfono'}
            </p>
          </div>
          <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 99, background: meta.bg, color: meta.color, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>{meta.label}</span>
        </div>

        <div style={{ padding: '18px 24px', overflowY: 'auto', flex: 1 }}>
          {/* Resumen del crédito */}
          <div style={{ background: 'var(--brand-50)', border: '1px solid var(--brand-300, var(--brand-200))', borderRadius: 10, padding: '14px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 800, color: 'var(--brand-700)', textTransform: 'uppercase', letterSpacing: 1.2 }}>Próxima cuota</p>
              <p style={{ margin: '6px 0 0', fontSize: 24, fontWeight: 800, color: 'var(--brand)' }}>{fmtQ(row.proxima_monto)}</p>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--ink-soft)' }}>{fmtFecha(row.proxima_vencimiento)} · {dpdTexto(row.dpd)}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: 0, fontSize: 11, color: 'var(--muted-foreground)' }}>Saldo capital</p>
              <p style={{ margin: '4px 0 0', fontSize: 15, fontWeight: 700 }}>{fmtQ(row.saldo_capital)}</p>
              <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--muted-foreground)' }}>{row.cuotas_pagadas}/{row.total_cuotas} pagadas</p>
            </div>
          </div>

          {/* Contacto — todos los canales para alcanzar al asociado, a mano */}
          <div style={{ border: '1px solid var(--hairline)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
              <div>
                <p style={{ margin: 0, fontSize: 11, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2 }}>Contacto</p>
                <p style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 600, fontFamily: row.telefono_e164 ? 'IBM Plex Mono, monospace' : 'inherit' }}>
                  {row.telefono_e164 ?? 'Sin teléfono registrado'}
                </p>
                {row.telefono_e164 && (
                  <span style={{
                    display: 'inline-block', marginTop: 6, fontSize: 10, fontWeight: 800,
                    padding: '2px 8px', borderRadius: 99, textTransform: 'uppercase', letterSpacing: 0.4,
                    background: verifAt ? 'rgba(16,185,129,0.12)' : 'rgba(217,119,6,0.12)',
                    color: verifAt ? 'var(--success-deep, #047857)' : 'var(--warning, #d97706)',
                  }}>
                    {verifAt ? '✓ Número verificado' : 'Sin verificar'}
                  </span>
                )}
              </div>
              {row.canal_preferido && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 99, background: 'var(--n-100)', color: 'var(--muted-foreground)', whiteSpace: 'nowrap' }}>
                  Prefiere {CANAL_LABEL[row.canal_preferido] ?? row.canal_preferido}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {tel && (
                <a href={tel} className="btn btn-ghost" style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12, fontWeight: 700 }}>
                  <Phone size={14} weight="bold" /> Llamar
                </a>
              )}
              {waSaludo && (
                <a href={waSaludo} target="_blank" rel="noreferrer"
                  onClick={() => registrarWa('Contacto por WhatsApp (saludo)')}
                  style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 10px', borderRadius: 8, background: '#25D366', color: '#fff', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
                  <WhatsappLogo size={14} weight="fill" /> WhatsApp
                </a>
              )}
            </div>
            {row.telefono_e164 && !verifAt && (
              <button
                onClick={marcarVerificado}
                disabled={verificando}
                className="btn btn-ghost"
                style={{ width: '100%', marginTop: 8, fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                {verificando ? 'Marcando…' : '✓ Marcar número verificado'}
              </button>
            )}
            {row.telefono_e164 && !row.whatsapp_optin && (
              <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--warning, #d97706)' }}>
                Sin opt-in de WhatsApp confirmado — usar con criterio.
              </p>
            )}
          </div>

          {/* Referencia personal (ADR 024): para ubicar al asociado cuando no responde. Queda como gestión 'otro'. */}
          <ReferenciasCobranza asociadoId={row.asociado_id} nombreAsociado={row.nombre_completo}
            onGestion={(nota) => {
              supabase.rpc('registrar_gestion_cobro', {
                p_credito_id: row.credito_id, p_canal: nota.startsWith('Llamada') ? 'llamada' : 'whatsapp', p_resultado: 'otro', p_notas: nota,
              }).then(() => onChange(), () => {})
            }} />

          {/* Mora — recargo acumulado + gestión de condonación (apoyo al asociado) */}
          {mora && (mora.mora_pendiente > 0 || mora.mora_condonada_total > 0) && (
            <div style={{ border: '1px solid rgba(163,45,45,0.22)', borderRadius: 10, padding: '12px 14px', marginBottom: 16, background: 'rgba(163,45,45,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div>
                  <p style={{ margin: 0, fontSize: 11, fontWeight: 800, color: 'var(--danger, #a32d2d)', textTransform: 'uppercase', letterSpacing: 1.2 }}>Mora acumulada</p>
                  <p style={{ margin: '6px 0 0', fontSize: 20, fontWeight: 800, color: mora.mora_pendiente > 0 ? 'var(--danger, #a32d2d)' : 'var(--ok-ink, #0f8a57)' }}>{fmtQ(mora.mora_pendiente)}</p>
                  {mora.mora_condonada_total > 0 && (
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--muted-foreground)' }}>Ya condonado: {fmtQ(mora.mora_condonada_total)}</p>
                  )}
                </div>
                {mora.mora_pendiente > 0 && (
                  <button onClick={() => setShowCondonar(true)} className="btn"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', color: 'var(--danger, #a32d2d)', border: '1px solid rgba(163,45,45,0.35)', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>
                    <HandHeart size={15} weight="bold" /> Gestionar mora
                  </button>
                )}
              </div>
              <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--muted-foreground)' }}>
                Condonar el recargo no borra el atraso ni toca capital/interés — solo quita la mora al asociado con intención de pago. Queda registrado.
              </p>
            </div>
          )}

          {/* Formas de pago — el único mensaje: cómo pagar + comprobante + nivel */}
          <div style={{ border: '1px solid var(--hairline)', borderRadius: 10, padding: '12px 14px', marginBottom: 18, background: 'var(--bg)' }}>
            <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2 }}>Formas de pago</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              {CUENTAS_PAGO.map((c) => (
                <div key={c.numero} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' }}>{c.banco} · {c.tipo}</span>
                  <span style={{ fontSize: 12.5, fontFamily: 'IBM Plex Mono, monospace', color: 'var(--ink-soft)' }}>{c.numero}</span>
                </div>
              ))}
              <div style={{ fontSize: 11.5, color: 'var(--muted-foreground)' }}>Efectivo: cualquier punto Akisí · RapiBAC: a la cuenta BAC</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted-foreground)' }}>Pago: sube el comprobante en la app (Reportar pago), o que nos pregunte.</div>
            </div>
            {waFormas && (
              <a href={waFormas} target="_blank" rel="noreferrer" className="btn"
                onClick={() => {
                  supabase.rpc('registrar_gestion_cobro', {
                    p_credito_id: row.credito_id, p_canal: 'whatsapp', p_resultado: 'contactado',
                    p_notas: 'Recordatorio de pago enviado por WhatsApp',
                  }).then(() => onChange(), () => {})
                }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', background: '#25D366', color: '#fff', border: 0, fontWeight: 700, fontSize: 13 }}>
                <WhatsappLogo size={15} weight="fill" /> Enviar por WhatsApp
              </a>
            )}
          </div>

          {/* Recordatorios manuales — mensaje pre-armado según el estado, envía + registra */}
          {row.telefono_e164 && row.estado_cobranza !== 'sin_pendiente' && (
            <div style={{ marginBottom: 18 }}>
              <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2 }}>Recordatorio</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {recordatoriosPara(row).map((rec) => {
                  const wa = waLink(row.telefono_e164, rec.texto)
                  if (!wa) return null
                  return (
                    <a key={rec.key} href={wa} target="_blank" rel="noreferrer"
                      title={rec.texto}
                      onClick={() => registrarWa(`Recordatorio (${rec.label}) enviado por WhatsApp`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', border: '1px solid var(--hairline)', borderRadius: 8, textDecoration: 'none', color: 'var(--ink)' }}>
                      <WhatsappLogo size={15} weight="fill" color="#25D366" />
                      <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600 }}>{rec.label}</span>
                      <span style={{ fontSize: 11, color: 'var(--muted-foreground)', whiteSpace: 'nowrap' }}>Enviar →</span>
                    </a>
                  )
                })}
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--muted-foreground)' }}>
                Abre WhatsApp con el texto listo y queda registrado como gestión.
              </p>
            </div>
          )}

          {/* Registrar gestión */}
          <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2 }}>Registrar gestión</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)' }}>
                Canal
                <select value={canal} onChange={(e) => setCanal(e.target.value)} style={selectStyle}>
                  {CANALES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </label>
              <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)' }}>
                Resultado
                <select value={resultado} onChange={(e) => setResultado(e.target.value)} style={selectStyle}>
                  {RESULTADOS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </label>
            </div>

            {esPromesa && (
              <div style={{ display: 'flex', gap: 8, padding: '10px 12px', background: 'rgba(15,138,87,0.06)', border: '1px solid rgba(15,138,87,0.22)', borderRadius: 8 }}>
                <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--ok-ink, #0f8a57)' }}>
                  Monto prometido
                  <input type="number" inputMode="decimal" value={promesaMonto} onChange={(e) => setPromesaMonto(e.target.value)} placeholder="0.00" style={inputStyle} />
                </label>
                <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--ok-ink, #0f8a57)' }}>
                  Fecha de pago
                  <input type="date" value={promesaFecha} onChange={(e) => setPromesaFecha(e.target.value)} style={inputStyle} />
                </label>
              </div>
            )}

            <textarea value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Notas de la gestión (opcional)…" rows={2}
              style={{ ...inputStyle, resize: 'vertical' }} />

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--muted-foreground)' }}>
              <ClockCountdown size={15} weight="bold" /> Próximo seguimiento
              <input type="date" value={proxSeg} onChange={(e) => setProxSeg(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
            </label>

            <button className="btn btn-primary" onClick={guardar} disabled={submitting}>
              {submitting ? 'Guardando…' : 'Guardar gestión'}
            </button>
          </div>

          {/* Historial */}
          <div style={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid var(--hairline)' }}>
            <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.2 }}>Historial</p>

            {hist && hist.promesas.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                {hist.promesas.map((p) => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 10px', border: '1px solid var(--hairline)', borderRadius: 8, marginBottom: 6 }}>
                    <div style={{ fontSize: 12 }}>
                      <span style={{ fontWeight: 700 }}>Promesa {fmtQ(p.monto)}</span> · {fmtFecha(p.fecha_promesa)}
                      <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 99, textTransform: 'uppercase',
                        background: p.estado === 'cumplida' ? 'rgba(15,138,87,0.12)' : p.estado === 'rota' ? 'rgba(163,45,45,0.10)' : 'var(--n-100)',
                        color: p.estado === 'cumplida' ? 'var(--ok-ink, #0f8a57)' : p.estado === 'rota' ? 'var(--danger, #a32d2d)' : 'var(--muted-foreground)' }}>{p.estado}</span>
                    </div>
                    {p.estado === 'vigente' && (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button onClick={() => cerrarPromesa(p.id, 'cumplida')} disabled={submitting} style={miniBtn('ok')}>Cumplida</button>
                        <button onClick={() => cerrarPromesa(p.id, 'rota')} disabled={submitting} style={miniBtn('danger')}>Rota</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {!hist ? <p style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>Cargando…</p>
              : hist.gestiones.length === 0 ? <p style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>Sin gestiones todavía.</p>
              : hist.gestiones.map((g) => (
                <div key={g.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--line-soft)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700 }}>{RESULTADO_LABEL[g.resultado] ?? g.resultado}</span>
                    <span style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{CANAL_LABEL[g.canal] ?? g.canal} · {fmtFechaHora(g.fecha)}</span>
                  </div>
                  {g.notas && <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--ink-soft)' }}>{g.notas}</p>}
                  {g.proximo_seguimiento && <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--muted-foreground)' }}>Seguimiento: {fmtFecha(g.proximo_seguimiento)}</p>}
                </div>
              ))}
          </div>
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--hairline)' }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ width: '100%' }}>Cerrar</button>
        </div>
      </aside>

      {showCondonar && mora && (
        <CondonarMoraModal
          row={row}
          moraPendiente={mora.mora_pendiente}
          onClose={() => setShowCondonar(false)}
          onDone={() => { setShowCondonar(false); void loadMora(); onChange() }}
        />
      )}
    </>
  )
}

// ============================================================
// Modal · condonar mora (100% / % / monto) con motivo obligatorio
// ============================================================
function CondonarMoraModal({ row, moraPendiente, onClose, onDone }: {
  row: BandejaRow; moraPendiente: number; onClose: () => void; onDone: () => void
}) {
  const [modo, setModo] = useState<'total' | 'parcial'>('total')
  const [tipoParcial, setTipoParcial] = useState<'pct' | 'monto'>('monto')
  const [valor, setValor] = useState('')
  const [motivo, setMotivo] = useState('')
  const [busy, setBusy] = useState(false)

  // pct efectivo a enviar al RPC (que trabaja por %).
  const pct = useMemo(() => {
    if (modo === 'total') return 100
    const v = Number(valor)
    if (!v || v <= 0) return 0
    if (tipoParcial === 'pct') return Math.min(100, v)
    // monto → % sobre la mora pendiente
    return Math.min(100, (v / moraPendiente) * 100)
  }, [modo, tipoParcial, valor, moraPendiente])

  const montoAplicar = useMemo(() => Math.round((moraPendiente * pct) / 100 * 100) / 100, [moraPendiente, pct])

  const confirmar = async () => {
    if (!motivo.trim()) { alert('El motivo es obligatorio (queda en el resguardo).'); return }
    if (pct <= 0) { alert('Ingresá un monto o porcentaje válido.'); return }
    setBusy(true)
    const { data, error } = await supabase.rpc('condonar_mora_credito', {
      p_credito_id: row.credito_id, p_pct: pct, p_motivo: motivo.trim(),
    })
    setBusy(false)
    if (error) { alert('Error: ' + error.message); return }
    const res = data as { condonado_total?: number } | null
    alert(`Mora condonada: ${fmtQ(res?.condonado_total ?? montoAplicar)}. Queda registrado.`)
    onDone()
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,18,25,0.55)', zIndex: 1001 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 'min(440px, 92vw)', background: '#fff', borderRadius: 14, zIndex: 1002, boxShadow: '0 24px 64px rgba(0,0,0,0.24)', overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--hairline)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Gestionar mora</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>{row.nombre_completo} · {row.no_asociado}</p>
        </div>
        <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 12px', background: 'rgba(163,45,45,0.05)', border: '1px solid rgba(163,45,45,0.18)', borderRadius: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--muted-foreground)', fontWeight: 700 }}>Mora pendiente</span>
            <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--danger, #a32d2d)' }}>{fmtQ(moraPendiente)}</span>
          </div>

          {/* Total vs parcial */}
          <div style={{ display: 'flex', gap: 8 }}>
            {(['total', 'parcial'] as const).map((m) => (
              <button key={m} onClick={() => setModo(m)} style={{
                all: 'unset', cursor: 'pointer', flex: 1, textAlign: 'center', padding: '10px 0', borderRadius: 8,
                fontSize: 13, fontWeight: 700, border: '1px solid',
                borderColor: modo === m ? 'var(--brand)' : 'var(--hairline)',
                background: modo === m ? 'var(--brand-50)' : 'transparent',
                color: modo === m ? 'var(--brand-700)' : 'var(--ink-soft)',
              }}>{m === 'total' ? 'Condonar todo' : 'Parcial'}</button>
            ))}
          </div>

          {modo === 'parcial' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                {(['monto', 'pct'] as const).map((t) => (
                  <button key={t} onClick={() => setTipoParcial(t)} style={{
                    all: 'unset', cursor: 'pointer', padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 700,
                    background: tipoParcial === t ? 'var(--brand)' : 'var(--n-100)', color: tipoParcial === t ? '#fff' : 'var(--muted-foreground)',
                  }}>{t === 'monto' ? 'Por monto (Q)' : 'Por %'}</button>
                ))}
              </div>
              <input type="number" inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)}
                placeholder={tipoParcial === 'monto' ? 'Monto a condonar (Q)' : 'Porcentaje (%)'} style={inputStyle} />
              <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-soft)' }}>
                Se condonará <strong>{fmtQ(montoAplicar)}</strong> · quedará pendiente {fmtQ(Math.max(0, moraPendiente - montoAplicar))}
              </p>
            </div>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 700, color: 'var(--muted-foreground)' }}>
            Motivo (obligatorio · queda en el resguardo contable)
            <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3}
              placeholder="Ej. Asociado con intención de pago; se apoya con la mora del atraso por…" style={{ ...inputStyle, resize: 'vertical' }} />
          </label>

          <p style={{ margin: 0, fontSize: 11, color: 'var(--muted-foreground)' }}>
            No borra el atraso ni toca capital/interés. El asociado sigue en mora hasta que pague la cuota; solo se le quita el recargo.
          </p>
        </div>
        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--hairline)', display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }} disabled={busy}>Cancelar</button>
          <button className="btn btn-primary" onClick={confirmar} style={{ flex: 1 }} disabled={busy || !motivo.trim() || pct <= 0}>
            {busy ? 'Condonando…' : `Condonar ${fmtQ(montoAplicar)}`}
          </button>
        </div>
      </div>
    </>
  )
}

const selectStyle: React.CSSProperties = { padding: '9px 10px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', background: '#fff' }
const inputStyle: React.CSSProperties = { padding: '9px 10px', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }
const miniBtn = (tone: 'ok' | 'danger'): React.CSSProperties => ({
  all: 'unset', cursor: 'pointer', fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 6,
  border: '1px solid', borderColor: tone === 'ok' ? 'rgba(15,138,87,0.35)' : 'rgba(163,45,45,0.30)',
  color: tone === 'ok' ? 'var(--ok-ink, #0f8a57)' : 'var(--danger, #a32d2d)',
})
